import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { checkoutApi } from '../services/api';
import { Lock, CreditCard, Truck, User, LogIn, Tag } from 'lucide-react';

const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedShippingAddress, setSelectedShippingAddress] = useState('new');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
  });
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Pre-fill form data when user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      // Set email from user account
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
      }));

      // If user has saved shipping addresses, select the first one by default
      if (user.shippingAddresses && user.shippingAddresses.length > 0) {
        setSelectedShippingAddress('0');
        const addr = user.shippingAddresses[0];
        setFormData(prev => ({
          ...prev,
          firstName: addr.firstName || user.firstName || '',
          lastName: addr.lastName || user.lastName || '',
          address: addr.address || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          country: addr.country || 'United States',
        }));
      }
    }
  }, [isAuthenticated, user]);

  // Handle saved address selection
  const handleAddressSelect = (value) => {
    setSelectedShippingAddress(value);
    
    if (value === 'new') {
      // Clear address fields but keep email and name
      setFormData(prev => ({
        ...prev,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States',
      }));
    } else {
      // Fill with selected saved address
      const index = parseInt(value);
      const addr = user.shippingAddresses[index];
      if (addr) {
        setFormData(prev => ({
          ...prev,
          firstName: addr.firstName || user?.firstName || '',
          lastName: addr.lastName || user?.lastName || '',
          address: addr.address || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          country: addr.country || 'United States',
        }));
      }
    }
  };

  // Apply coupon for free shipping on orders under $30
  const handleApplyCoupon = () => {
    setCouponError('');
    if (couponCode.toUpperCase() === 'FREEDEL2026') {
      if (cartTotal < 30) {
        setCouponApplied(true);
        setCouponError('');
      } else {
        setCouponError('This coupon is only valid for orders under $30');
      }
    } else {
      setCouponError('Invalid coupon code');
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(false);
    setCouponError('');
  };

  // Calculate shipping - free if over $30 OR if coupon applied
  const shipping = cartTotal >= 30 ? 0 : (couponApplied ? 0 : 8.95);
  // Tax will be calculated by Stripe at checkout
  const estimatedTotal = cartTotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const checkoutData = {
        customer: formData,
        items: cart.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          name: item.name,
          size: item.size,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        subtotal: cartTotal,
        shipping,
        tax: 0,  // Tax calculated by Stripe
        total: estimatedTotal
      };

      const response = await checkoutApi.createSession(checkoutData);
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        clearCart();
        window.location.href = response.data.url;
      } else {
        // Fallback for when Stripe is not configured
        clearCart();
        navigate(`/order-confirmation?order_id=${response.data.orderId}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Your cart is empty</h1>
          <Button onClick={() => navigate('/products')} className="bg-[#2d4a47] hover:bg-[#1e3633] rounded-none">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-serif text-center mb-8">Checkout</h1>

        {/* Login prompt for guests */}
        {!isAuthenticated && (
          <div className="max-w-2xl mx-auto mb-8 bg-[#f5f1eb] border border-[#2d4a47]/20 px-6 py-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="text-[#2d4a47]" size={20} />
                <div>
                  <p className="text-sm font-medium text-gray-800">Have an account?</p>
                  <p className="text-xs text-gray-600">Log in for faster checkout with saved addresses</p>
                </div>
              </div>
              <Link to="/login?redirect=/checkout">
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <LogIn size={14} /> Log In
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Logged in user info */}
        {isAuthenticated && (
          <div className="max-w-2xl mx-auto mb-8 bg-[#eef3f0] border border-[#2d4a47]/20 px-6 py-4 rounded-lg">
            <div className="flex items-center gap-3">
              <User className="text-[#2d4a47]" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Welcome back, {user?.firstName}!
                </p>
                <p className="text-xs text-gray-600">
                  Your account information has been pre-filled below
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="max-w-2xl mx-auto mb-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <div className="bg-white p-8 shadow-sm">
            <form onSubmit={handleSubmit}>
              {/* Contact Information */}
              <div className="mb-8">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#2d4a47] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Contact Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                      disabled={isAuthenticated}
                    />
                    {isAuthenticated && (
                      <p className="text-xs text-gray-500 mt-1">Using your account email</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mb-8">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Truck size={20} />
                  Shipping Address
                </h2>

                {/* Saved addresses dropdown for logged in users */}
                {isAuthenticated && user?.shippingAddresses?.length > 0 && (
                  <div className="mb-4">
                    <Label>Use Saved Address</Label>
                    <Select value={selectedShippingAddress} onValueChange={handleAddressSelect}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select an address" />
                      </SelectTrigger>
                      <SelectContent>
                        {user.shippingAddresses.map((addr, index) => (
                          <SelectItem key={index} value={String(index)}>
                            {addr.address}, {addr.city}, {addr.state} {addr.zip}
                          </SelectItem>
                        ))}
                        <SelectItem value="new">+ Enter new address</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      name="state"
                      required
                      value={formData.state}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP Code</Label>
                    <Input
                      id="zip"
                      name="zip"
                      required
                      value={formData.zip}
                      onChange={handleInputChange}
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      disabled
                      className="rounded-none mt-1 bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="mb-8">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <CreditCard size={20} />
                  Payment
                </h2>
                <div className="bg-gray-50 p-4 border border-gray-200 text-sm text-gray-600">
                  <p>You will be redirected to Stripe's secure checkout to complete your purchase.</p>
                  <p className="mt-2 text-xs flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Your payment information is securely processed by Stripe.
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2d4a47] hover:bg-[#1e3633] text-white rounded-none py-6 text-sm tracking-wider"
              >
                {loading ? 'Processing...' : `PROCEED TO PAYMENT - $${estimatedTotal.toFixed(2)}`}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center gap-1">
                <Lock size={12} />
                Secure checkout powered by Stripe
              </p>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-8 shadow-sm">
              <h2 className="text-lg font-medium mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={`${item.productId}-${item.variantId}`} className="flex gap-4">
                    <img
                      src={item.image}
                      alt="Natural and organic skincare product"
                      className="w-16 h-16 object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium">{item.name}</h4>
                      <p className="text-xs text-gray-500">{item.size} × {item.quantity}</p>
                    </div>
                    <span className="text-sm font-medium">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-3">
                {/* Coupon Code Input */}
                <div className="pb-3 border-b">
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Tag size={14} /> Coupon Code
                  </Label>
                  {!couponApplied ? (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Enter code"
                        className="flex-1 text-sm"
                      />
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm"
                        onClick={handleApplyCoupon}
                      >
                        Apply
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 p-2 rounded">
                      <span className="text-sm text-green-700 font-medium">
                        ✓ FREEDEL2026 - Free Delivery Applied
                      </span>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={handleRemoveCoupon}
                        className="text-red-600 hover:text-red-700 h-auto p-1"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  {couponError && (
                    <p className="text-xs text-red-600 mt-1">{couponError}</p>
                  )}
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className={couponApplied && cartTotal < 30 ? 'text-green-600' : ''}>
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span className="text-gray-500 italic">Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-lg font-medium pt-3 border-t">
                  <span>Estimated Total</span>
                  <span>${estimatedTotal.toFixed(2)}+</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Final tax will be calculated based on your shipping address.
                </p>
              </div>
            </div>

            {shipping > 0 && !couponApplied && (
              <p className="text-sm text-gray-600 mt-4 text-center">
                Add ${(30 - cartTotal).toFixed(2)} more for free shipping!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
